import { Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Hält den vom Coach gewählten "aktiven Workspace". Wird vom
 * activeWorkspaceInterceptor als Header `X-Active-Workspace-Id` an die API
 * angehängt, damit geteilte Spezialtool-Daten (Team Pursuit / Sprint) auf den
 * richtigen Workspace gescopt werden und der Sportler-Umschalter in der Topbar
 * nur dessen Sportler zeigt. Der Server validiert die ID gegen die
 * Coach-Mitgliedschaft (kein Spoofing) und fällt bei null auf den einzigen
 * Workspace bzw. Owner-Scope zurück.
 */
@Injectable({ providedIn: 'root' })
export class ActiveWorkspaceService {
  private readonly STORAGE_KEY = 'activeWorkspaceId';

  readonly workspaceId = signal<number | null>(this.readInitial());

  private readonly changedSubject = new Subject<number | null>();

  /**
   * Feuert SYNCHRON nach einem echten Wechsel (nicht bei erneuter Wahl
   * desselben Workspace), wenn der Signalwert schon steht -- ein Request aus
   * dem Handler trägt also bereits den neuen Header. Bewusst kein
   * toObservable(): das liefe erst in der nächsten Change Detection.
   */
  readonly changed$: Observable<number | null> = this.changedSubject.asObservable();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === this.STORAGE_KEY) {
          this.apply(this.parseId(e.newValue));
        }
      });
    }
  }

  /** Synchroner Snapshot (für den Interceptor). */
  snapshot(): number | null {
    return this.workspaceId();
  }

  set(id: number | null | undefined): void {
    const next = this.parseId(id);
    if (next === null) {
      localStorage.removeItem(this.STORAGE_KEY);
    } else {
      localStorage.setItem(this.STORAGE_KEY, String(next));
    }
    this.apply(next);
  }

  reset(): void {
    this.set(null);
  }

  private apply(next: number | null): void {
    if (next === this.workspaceId()) return;
    this.workspaceId.set(next);
    this.changedSubject.next(next);
  }

  private readInitial(): number | null {
    try {
      return this.parseId(localStorage.getItem(this.STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private parseId(val: unknown): number | null {
    if (val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  }
}
